// CustomTagSelector — multi-select chip cloud, free-form. Suggestions
// appear from the org's existing pool below the input. Typing + Enter
// auto-promotes the chip via DP2 (server creates a canonical
// inventory_tag_custom row + rel link).
//
// Ported from tag-selectors.jsx (CustomTagSelector at lines 495-600).

import * as React from "react";

const XMark = ({ size = 9 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

type Props = {
  /** Currently-attached custom tag display texts. */
  value: string[];
  onAdd: (displayText: string) => void;
  onRemove: (displayText: string) => void;
  suggestionPool?: string[];
};

export function CustomTagSelector({
  value,
  onAdd,
  onRemove,
  suggestionPool = [],
}: Props) {
  const [draft, setDraft] = React.useState("");
  const [focused, setFocused] = React.useState(false);

  const commit = (t: string) => {
    const v = t.trim();
    if (!v) return;
    if (value.includes(v)) return;
    onAdd(v);
    setDraft("");
  };

  const suggestions = suggestionPool
    .filter((p) => !value.includes(p))
    .filter(
      (p) =>
        !draft.trim() || p.toLowerCase().includes(draft.trim().toLowerCase()),
    )
    .slice(0, 8);

  return (
    <div className="vstack" style={{ gap: 8 }}>
      <div
        style={{
          padding: 8,
          borderRadius: 6,
          border: `1px solid ${focused ? "var(--ink)" : "var(--line-strong)"}`,
          background: "var(--panel)",
          boxShadow: focused ? "0 0 0 2px var(--accent-soft)" : "none",
          display: "flex",
          flexWrap: "wrap",
          gap: 5,
          alignItems: "center",
          minHeight: 36,
        }}
      >
        {value.map((t) => (
          <span
            key={t}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "3px 4px 3px 10px",
              borderRadius: 999,
              background: "var(--accent-soft)",
              color: "var(--accent-ink)",
              border: "1px solid var(--accent-line)",
              fontSize: 11.5,
              fontWeight: 500,
            }}
          >
            {t}
            <button
              onClick={() => onRemove(t)}
              aria-label={`Remove ${t}`}
              style={{
                display: "inline-flex",
                padding: 2,
                borderRadius: 999,
                background: "transparent",
                border: "none",
                color: "var(--accent-ink)",
                cursor: "pointer",
              }}
            >
              <XMark size={9} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(draft);
            }
            if (e.key === ",") {
              e.preventDefault();
              commit(draft);
            }
            if (e.key === "Backspace" && !draft && value.length > 0) {
              const last = value[value.length - 1]!;
              onRemove(last);
            }
          }}
          placeholder={value.length === 0 ? "Type a tag and press Enter…" : ""}
          style={{
            flex: 1,
            minWidth: 120,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 12.5,
            padding: "3px 4px",
          }}
        />
      </div>

      {focused && suggestions.length > 0 && (
        <div className="vstack" style={{ gap: 4 }}>
          <span
            className="mono"
            style={{
              fontSize: 9,
              color: "var(--ink-4)",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              fontWeight: 600,
            }}
          >
            From your org's pool
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commit(s)}
                style={{
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "transparent",
                  border: "1px dashed var(--line-strong)",
                  color: "var(--ink-3)",
                  fontSize: 11,
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--accent-soft)";
                  e.currentTarget.style.borderStyle = "solid";
                  e.currentTarget.style.color = "var(--accent-ink)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.borderStyle = "dashed";
                  e.currentTarget.style.color = "var(--ink-3)";
                }}
              >
                + {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
